import { z } from 'zod/v4';

/**
 * Normalized descriptor for a single registry-specific metadata field the user must fill
 * before pushing a registry action (cancellation / transfer). This is NOT an API shape: it is produced
 * by adapting the JSON Schema returned by
 * `GET /registries/{registry}/registry-actions/{action_type}/metadata-schema` into something the
 * dynamic form renderer can consume.
 */
export interface RegistryActionMetadataField {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'managed-enum' | 'country' | 'date-range';
  required: boolean;
  defaultValue?: unknown;
  options?: { value: string; label: string }[] | null;
  // For a `managed-enum` field: the managed-enum `type` whose values back the selectable, so the
  // create action can register new values against it.
  managedEnumType?: string;
  // For a `date-range` field: the two underlying metadata keys the single control writes to, plus
  // each end's prefill default. The submitted metadata still carries `startKey`/`endKey` separately.
  startKey?: string;
  endKey?: string;
  startDefault?: unknown;
  endDefault?: unknown;
}

export const getRegistryActionMetadataSchemaResponseSchema = z.record(z.string(), z.unknown());

export type GetRegistryActionMetadataSchemaResponse = z.infer<typeof getRegistryActionMetadataSchemaResponseSchema>;

export const registrySchema = z.object({
  identifier: z.string(),
  label: z.string(),
  // Pooled and non-pooled rounding give identical results on a volumetric registry
  is_volumetric: z.boolean(),
  supports_registry_action_push: z.boolean().optional(),
  supported_push_action_types: z.array(z.string()).optional(),
});

export const registriesSchema = z.array(registrySchema);

export type Registries = z.infer<typeof registriesSchema>;
